import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { catchError, EMPTY, map, of, switchMap } from 'rxjs';
import { AppRoute } from '../app-route';
import { injectLogger } from '../logging/logger-injection';
import { GET_SCAN_PROJECT_URL } from '../utils/get-scan-project-url';
import { Role, Status, User } from './user.models';
import { SetUser } from './user.state';

interface UserResponse {
  /** Connect user id. Not the same as TID. */
  id: string;

  /** Trimble Identity - TID. */
  tidUuid: string;

  title?: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  language?: string;
  status: Status;
  role: Role;
  roleClaims?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly getScanProjectUrl$ = inject(GET_SCAN_PROJECT_URL);
  private readonly router = inject(Router);
  private readonly logger = injectLogger('UserService');

  constructor(
    private http: HttpClient,
    private store: Store,
  ) {}

  getUsers() {
    return this.getScanProjectUrl$('/users').pipe(
      switchMap((url) => {
        return this.http.get<UserResponse[]>(url);
      }),
      map((users) => users.map((user) => this.mapUser(user))),
    );
  }

  loadUser() {
    return this.getCurrentUser().pipe(
      switchMap((user) => this.store.dispatch(new SetUser(user)).pipe(map(() => user))),
      switchMap((user) => {
        if (user.role === Role.NoAccess) {
          this.logger.error('NoAccess: User does not have access to project.');
          this.router.navigate([AppRoute.NoAccess]);

          // Return an empty observable to complete the stream
          return EMPTY;
        }
        return of(user);
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.status === HttpStatusCode.Forbidden) {
          this.logger.error('NoAccess: User does not have access to fetch user info.', {}, err);
          this.router.navigate([AppRoute.NoAccess]);
        }

        throw err;
      }),
    );
  }

  private getCurrentUser() {
    return this.getScanProjectUrl$('/users/me').pipe(
      switchMap((url) => this.http.get<UserResponse>(url)),
      map((user) => this.mapUser(user)),
    );
  }

  private mapUser(user: UserResponse): User {
    return {
      id: user.tidUuid,
      connectId: user.id,
      title: user.title,
      firstName: user.firstName,
      lastName: user.lastName,
      fullName: user.fullName,
      email: user.email,
      language: user.language,
      status: user.status,
      role: user.role,
      roleClaims: user.roleClaims,
    };
  }
}
